import dataService from "flow/data/service";
import manifestManager from "flow/utils/manifestManager";
import metaCache from "flow/utils/metaCache";
import sysLogger from "flow/sysLogger";
import scriptPathManager from "flow/init/scriptPathManager";
import authorizationManagerFactory from "flow/factories/authorizationManagerFactory";
import cssLoader from "flow/utils/cssLoader";
import * as splashStore from "flow/splash/splashStore";
import moduleLoader from "flow/wrappers/moduleLoader";
import config from "flow/config";
import MyFlow from "flow/MyFlow";
import { WebModuleManifest } from "flow/entities/WebModuleManifest";
import WebModuleEntry from "flow/manifests/WebModuleEntry";
import { Dictionary } from "flow/api/types";
import protect from "flow/protect";
import environment from "flow/configuration/environment";

type FailedModule = { moduleName: string; id: string };
type SharedModuleEntry = { key: string; value: JQueryPromise<any> };

const failedModules: FailedModule[] = [];
let onDemandSharedModules: WebModuleEntry[];
const sharedModulePromiseDictionary: SharedModuleEntry[] = [];

export function load(myFlow: MyFlow, webModuleDefinitions: WebModuleEntry[]) {
    const dfd = myFlow.deferred();
    const whenArgs: JQueryPromise<any>[] = [];

    splashStore.reset(splashStore.SplashMode.WebModules);

    onDemandSharedModules = webModuleDefinitions.filter(function (wm) {
        return wm.onDemandOnly;
    });

    webModuleDefinitions.forEach(moduleDefinition => {
        splashStore.addEntry({ id: moduleDefinition.id, name: moduleDefinition.name });

        if (onDemandSharedModules.indexOf(moduleDefinition) > -1) {
            return;
        }

        if (manifestManager.isExcludedWebModule(moduleDefinition.id)) {
            // Module is excluded by user.
            // Add for filtering, but don't call it.
            manifestManager.addWebModule(
                moduleDefinition.id,
                moduleDefinition.name,
                moduleDefinition.url,
                [],
                undefined,
                false,
                [],
                moduleDefinition.moduleKeyName,
                moduleDefinition.onDemandOnly,
                undefined
            );
            myFlow.log("Module " + moduleDefinition.id + " excluded by user.");
            splashStore.setDone(moduleDefinition.id);
        } else {
            whenArgs.push(readWebModule(myFlow, moduleDefinition, false));
        }
    });

    myFlow.when(...whenArgs).always(() => {
        if (failedModules.length === 0 || environment.get<boolean>("ignoreBootErrors") === true) {
            dfd.resolve();
        } else {
            showContinueDialog(myFlow, failedModules)
                .done(pickedYes => {
                    if (pickedYes === true) {
                        dfd.resolve();
                    } else {
                        dfd.reject();
                        myFlow.refreshFlow(true);
                    }
                })
                .fail(function () {
                    dfd.reject();
                });
        }
    });
    return dfd.promise();
}

function readWebModule(myFlow: MyFlow, moduleDefinition: WebModuleEntry, skipEmptyWebModuleCheck: boolean) {
    const dfd = myFlow.deferred<void>();
    manifestManager.addWebModule(
        moduleDefinition.id,
        moduleDefinition.name,
        moduleDefinition.url,
        [],
        undefined,
        false,
        [],
        moduleDefinition.moduleKeyName,
        moduleDefinition.onDemandOnly,
        undefined
    );

    dataService
        .getWebModule(myFlow, moduleDefinition.url)
        .done(function (webModuleManifest) {
            if (webModuleManifest.inactiveReason) {
                splashStore.setDone(moduleDefinition.id);
                manifestManager.flagWebModuleAsInactive(moduleDefinition.id, webModuleManifest.inactiveReason);
                myFlow.notifyInfoSystem(
                    webModuleManifest.inactiveReason,
                    myFlow.translate("FlowHtml.Translations.ModuleNotLoaded") + " " + moduleDefinition.name
                );
                dfd.resolve();
                return;
            }

            //Check if this web module actually has something
            if (!skipEmptyWebModuleCheck && isWebModuleManifestEmpty(webModuleManifest)) {
                splashStore.setDone(moduleDefinition.id);
                manifestManager.flagWebModuleAsEmpty(moduleDefinition.id);
                dfd.resolve();
                return;
            }

            let onDemandSharedModule: WebModuleEntry;
            if (
                moduleDefinition.dependent &&
                moduleDefinition.dependent.length > 0 &&
                moduleDefinition.dependent != "undefined"
            ) {
                onDemandSharedModule = onDemandSharedModules.filter(function (webModuleDefinition) {
                    return webModuleDefinition.id == moduleDefinition.dependent;
                })[0];
            }

            if (onDemandSharedModule) {
                loadOnDemandSharedWebModule(myFlow, onDemandSharedModule).done(function () {
                    const failedModule = failedModules.filter(function (failedModule) {
                        return failedModule.id == onDemandSharedModule.id;
                    })[0];

                    if (failedModule) {
                        handleFailedModule(myFlow, moduleDefinition);
                        splashStore.setFailed(moduleDefinition.id);
                        dfd.resolve();
                        return;
                    }
                    loadCssAndScripts(myFlow, moduleDefinition, webModuleManifest, dfd);
                });
            } else {
                loadCssAndScripts(myFlow, moduleDefinition, webModuleManifest, dfd);
            }
        })
        .fail(function () {
            splashStore.setFailed(moduleDefinition.id);
            handleFailedModule(myFlow, moduleDefinition);
            dfd.resolve();
        });
    return dfd.promise();
}

function loadCssAndScripts(
    myFlow: MyFlow,
    moduleDefinition: WebModuleEntry,
    webModuleManifest: WebModuleManifest,
    dfd: JQueryDeferred<any>
) {
    //Add the client log level
    sysLogger.addClientLogLevels(moduleDefinition.id, moduleDefinition.logLevel);

    mergeMetaConfigs(webModuleManifest.metaConfigs);
    loadCss(moduleDefinition.url, webModuleManifest.cssUrls, webModuleManifest.cacheBuster);

    scriptPathManager.registerPaths(
        moduleDefinition.url,
        webModuleManifest.scriptPathConfigs,
        webModuleManifest.cacheBuster
    );

    manifestManager.addLicenseBundles(webModuleManifest.licenseBundles);

    function errorHandler(error: Error) {
        if (error) {
            console.error(error);
        }

        handleFailedModule(myFlow, moduleDefinition);

        splashStore.setFailed(moduleDefinition.id);
        dfd.resolve();
    }

    loadScriptBundleIfAny(webModuleManifest.scriptBundle)
        .then(bundle => {
            if (
                webModuleManifest.authorizationManager &&
                webModuleManifest.authorizationManager !== undefined &&
                webModuleManifest.authorizationManager !== ""
            ) {
                createAuthorizationManager(myFlow, webModuleManifest, moduleDefinition)
                    .done(authorizationManager => {
                        addManifests(moduleDefinition, webModuleManifest, authorizationManager);
                        if (bundle && bundle.onLoad) {
                            protect(myFlow, bundle, bundle.onLoad)(moduleDefinition.url);
                        }
                        splashStore.setDone(moduleDefinition.id);
                        dfd.resolve();
                    })
                    .fail(errorHandler);
            } else {
                addManifests(moduleDefinition, webModuleManifest, undefined);
                if (bundle && bundle.onLoad) {
                    protect(myFlow, bundle, bundle.onLoad)(moduleDefinition.url);
                }
                splashStore.setDone(moduleDefinition.id);
                dfd.resolve();
            }
        })
        .catch(errorHandler);
}

function loadCss(baseUrl: string, cssUrls: string[], cacheBuster: string) {
    if (cssUrls) {
        for (let i = 0; i < cssUrls.length; i++) {
            cssUrls[i] = baseUrl + cssUrls[i];
        }
        cssLoader.loadBatch(cssUrls, cacheBuster);
    }
}

function mergeMetaConfigs(metaConfigs: Dictionary[]) {
    if (metaConfigs) {
        metaConfigs.forEach(metaConfig => metaCache.add(metaConfig));
    }
}

function loadOnDemandSharedWebModule(myFlow: MyFlow, onDemandSharedWebModuleDefinition: WebModuleEntry) {
    if (
        sharedModulePromiseDictionary[0] &&
        sharedModulePromiseDictionary[0]["key"] == onDemandSharedWebModuleDefinition["id"]
    ) {
        return sharedModulePromiseDictionary[0]["value"];
    }

    const promise = readWebModule(myFlow, onDemandSharedWebModuleDefinition, true);
    sharedModulePromiseDictionary.push({
        key: onDemandSharedWebModuleDefinition["id"],
        value: promise
    });

    return promise;
}

function loadScriptBundleIfAny(bundlePath: string) {
    return new Promise<any>((resolve, reject) => {
        if (bundlePath) {
            moduleLoader.requireSingleAsync(bundlePath).then(resolve).catch(reject);
        } else {
            resolve(undefined);
        }
    });
}

function addManifests(
    moduleDefinition: WebModuleEntry,
    webModuleManifest: WebModuleManifest,
    authorizationManager: any
) {
    manifestManager.setDescription(moduleDefinition.id, webModuleManifest.moduleDescription);
    manifestManager.addWebModule(
        moduleDefinition.id,
        moduleDefinition.name,
        moduleDefinition.url,
        webModuleManifest.moduleRoles,
        authorizationManager,
        webModuleManifest.isRoleSelectionExclusive,
        webModuleManifest.impersonatedUser,
        moduleDefinition.moduleKeyName,
        moduleDefinition.onDemandOnly,
        webModuleManifest.cacheBuster
    );

    manifestManager.addManifests(
        moduleDefinition.id,
        webModuleManifest.menuManifests,
        webModuleManifest.allowResizeComponents
    );
    manifestManager.addManifests(
        moduleDefinition.id,
        webModuleManifest.componentManifests,
        webModuleManifest.allowResizeComponents
    );
    manifestManager.addManifests(
        moduleDefinition.id,
        webModuleManifest.contextManifests,
        webModuleManifest.allowResizeComponents
    );
    manifestManager.addManifests(
        moduleDefinition.id,
        webModuleManifest.dialogManifests,
        webModuleManifest.allowResizeComponents
    );
    manifestManager.addManifests(
        moduleDefinition.id,
        webModuleManifest.externalComponentManifests,
        webModuleManifest.allowResizeComponents
    );
    manifestManager.addManifests(
        moduleDefinition.id,
        webModuleManifest.sideTabManifest,
        webModuleManifest.allowResizeComponents
    );

    manifestManager.addCommandScripts(webModuleManifest.commandScripts);
    manifestManager.addManifests(
        moduleDefinition.id,
        webModuleManifest.userContextTabManifests,
        webModuleManifest.allowResizeComponents
    );
}

function createAuthorizationManager(
    myFlow: MyFlow,
    webModuleManifest: WebModuleManifest,
    moduleDefinition: WebModuleEntry
) {
    const dfd = myFlow.deferred();

    authorizationManagerFactory
        .createAuthorizationManager(myFlow, webModuleManifest.authorizationManager)
        .done(authorizationManager => {
            if (authorizationManager.initialize) {
                const promise = protect(
                    myFlow,
                    authorizationManager,
                    authorizationManager.initialize
                )(myFlow, moduleDefinition.url);
                if (promise) {
                    promise
                        .done(() => {
                            dfd.resolve(authorizationManager);
                        })
                        .fail(() => {
                            dfd.reject();
                        });
                } else {
                    dfd.reject();
                }
            } else {
                dfd.resolve(authorizationManager);
            }
        })
        .fail(() => {
            dfd.reject();
        });

    return dfd.promise();
}

function handleFailedModule(myFlow: MyFlow, moduleDefinition: WebModuleEntry) {
    manifestManager.flagModuleAsBroken(moduleDefinition.id);
    const moduleName = myFlow.translate(moduleDefinition.name);
    const moduleId = moduleDefinition.id;
    const errorString = metaCache.lookup(
        "FlowHtml.Translations.FailedToReadModule",
        "FlowHtml.Translations.FailedToReadModule",
        moduleName
    );
    myFlow.error(errorString);
    myFlow.logObjectsAsErrors(moduleDefinition);

    if (moduleDefinition.id === config.systemInfoModuleId) {
        return; // Silent fail, user can still perform ordinary tasks
    }

    myFlow.notifyError(errorString);

    failedModules.push({ moduleName: moduleName, id: moduleId });
}

function showContinueDialog(myFlow: MyFlow, failedReads: FailedModule[]) {
    let errorText = myFlow.translate("FlowHtml.Translations.NotAllModulesCouldBeLoaded") + "<br>";

    failedReads.forEach(module => {
        errorText += module.moduleName + "<br>";
    });

    errorText += "<br>" + myFlow.translate("FlowHtml.Translations.DoYouStillWantToContinue");

    return myFlow.showConfirmDialog(
        myFlow.translate("FlowHtml.Translations.OhNo"),
        errorText,
        myFlow.translate("FlowHtml.Translations.Continue"),
        myFlow.translate("FlowHtml.Translations.TryAgain")
    );
}

function isWebModuleManifestEmpty(webModuleManifest: WebModuleManifest) {
    if (webModuleManifest.loadEvenIfEmpty) {
        return false;
    }

    return (
        webModuleManifest.componentManifests.length === 0 &&
        webModuleManifest.contextManifests.length === 0 &&
        webModuleManifest.menuManifests.length === 0 &&
        webModuleManifest.dialogManifests.length === 0 &&
        webModuleManifest.externalComponentManifests.length === 0
    );
}
